import { useEffect } from 'react';
import { RouterProvider, createBrowserRouter, Outlet } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { MediaQueries, PAGE_INFO } from 'src/util/constants';
import SecureRoute from './util/SecureRoute';
import GeneralError from './components/common/GeneralError/GeneralError';
import ParcelSearchLayoutV1 from './components/Layouts/ParcelSearchV1/ParcelSearchLayout/ParcelSearchLayout';
import HomeView from './components/Views/ParcelSearchV1/HomeView/HomeView';
import ParcelDetailViewV1 from './components/Views/ParcelSearchV1/ParcelDetailView/ParcelDetailView';
import ParcelSearchLayout from './components/Layouts/ParcelSearchLayout/ParcelSearchLayout';
import ParcelSearch from './components/Views/ParcelSearch/ParcelSearch';
import ParcelDetailView from './components/Views/ParcelDetailView/ParcelDetailView';
import HealthCheck from './components/Views/HealthCheck';
import ShippingPortal from './components/Views/ShippingPortal/ShippingPortal';
import EditLabelView from './components/Views/EditLabelView/EditLabelView';
import EditManifestView from './components/Views/EditManifestView/EditManifestView';
import {
    shippingPortalEnabled,
    userManagementEnabled,
    reportsEnabled,
    MaintenanceModeEnabled,
    parcelSearchV2Enabled,
    parcelSearchV1Enabled,
} from './util/featureFlags';
import ShippingPortalLayout from './components/Layouts/ShippingPortalLayout/ShippingPortalLayout';
import LayoutContainer from './components/Layouts/LayoutContainer/LayoutContainer';
import UserManagementLayout from './components/Layouts/UserManagementLayout/UserManagementLayout';
import ShipmentTrackerLayout from './components/Layouts/ShipmentTrackerLayout/ShipmentTrackerLayout';
import StopDetails from './components/Views/ShipmentTracker/StopDetails/StopDetails';
import ManageUsers from './components/Views/AdminView/ManageUsers';
import User from './components/Views/AdminView/User';
import ReportsLayout from './components/Layouts/ReportsLayout/ReportsLayout';
import PowerBiReport from './components/Views/Reports/PowerBi/PowerBiReport';
import OSD from './components/Views/Reports/OSDManifest/OSDManifest';
import DeliveryManagementLayout from './components/Layouts/DeliveryManagement/DeliveryManagementLayout';
import DeliveryManagement from './components/Views/DeliveryManagement/DeliveryManagement';
import Loading from './components/common/SnackBars/Loading';
import Maintenance from './components/Views/Maintenance/Maintenance';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider } from 'src/components/common/ThemeProvider/ThemeProvider';
import { LayoutContainerContextProvider } from 'src/components/Layouts/LayoutContainer/LayoutContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './App.scss';

// Wrapper provides components with access to the queryClient
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // With garbage collection time set to 0, query cache will never get stored.
            gcTime: 0,
            // false -> failed queries will not retry by default
            retry: false,
        },
    },
});

export default function App() {
    if (MaintenanceModeEnabled) {
        return (
            <ThemeProvider>
                <Maintenance />
            </ThemeProvider>
        );
    }

    const isTablet = useMediaQuery(MediaQueries.Tablet);
    const isPhone = useMediaQuery(MediaQueries.Phone);

    const LayoutPlain = () => (
        <>
            <Outlet />
        </>
    );

    const getParcelV2Search = () => {
        if (parcelSearchV2Enabled) {
            return [
                {
                    element: <ParcelSearchLayout />,
                    children: [
                        {
                            index: true,
                            element: <ParcelSearch />,
                        },
                        {
                            path: 'parcel/:barcode/:date/:customerId',
                            element: <ParcelDetailView />,
                        },
                    ],
                },
            ];
        } else {
            return [
                {
                    element: <ParcelSearchLayoutV1 />,
                    children: [
                        {
                            index: true,
                            element: <HomeView />,
                        },
                        {
                            path: 'parcel/:barcode/:date/:customerId/:endpointName',
                            element: <ParcelDetailViewV1 />,
                        },
                    ],
                },
            ];
        }
    };

    const getV1ParcelSearch = () => {
        if (parcelSearchV1Enabled) {
            return [
                {
                    path: 'v1',
                    element: <ParcelSearchLayoutV1 />,
                    children: [
                        {
                            path: 'parcelSearch',
                            element: <HomeView />,
                        },
                        {
                            path: 'parcel/:barcode/:date/:customerId/:endpointName',
                            element: <ParcelDetailViewV1 />,
                        },
                    ],
                },
            ];
        }

        return [];
    };

    const getShippingPortal = () => {
        if (shippingPortalEnabled) {
            return [
                {
                    path: 'shipping-portal',
                    element: <ShippingPortalLayout />,
                    children: [
                        {
                            index: true,
                            element: <ShippingPortal />,
                        },
                        {
                            path: 'label/:id',
                            element: <EditLabelView />,
                        },
                        {
                            path: 'manifest/:id',
                            element: <EditManifestView />,
                        },
                    ],
                },
            ];
        }

        return [];
    };

    const getUserManagement = () => {
        if (userManagementEnabled) {
            return [
                {
                    path: 'admin',
                    element: <UserManagementLayout />,
                    children: [
                        {
                            path: 'users',
                            element: <ManageUsers />,
                        },
                        {
                            path: 'user',
                            children: [
                                {
                                    path: ':userId',
                                    element: <User />,
                                },
                            ],
                        },
                    ],
                },
            ];
        }

        return [];
    };

    const getReports = () => {
        if (reportsEnabled) {
            return [
                {
                    path: 'reports',
                    element: <ReportsLayout />,
                },
                {
                    path: '/reports/:reportName',
                    element: <PowerBiReport />,
                    errorElement: <GeneralError />,
                },
                {
                    path: '/reports/fmtp/osd',
                    element: <OSD />,
                    errorElement: <GeneralError />,
                },
            ];
        }

        return [];
    };

    // Only top level paths should start with '/'
    const routes = [
        {
            element: <LayoutPlain />,
            children: [
                {
                    path: '/fmtp-health-check-secure',
                    element: <HealthCheck />,
                    errorElement: <GeneralError />,
                },
            ],
        },
        {
            element: <LayoutPlain />,
            children: [
                {
                    path: '/fmtp-health-check',
                    element: <HealthCheck />,
                    errorElement: <GeneralError />,
                },
            ],
        },
        {
            path: '/',
            element: (
                <SecureRoute reroutePage={PAGE_INFO.parcelSearch.name}>
                    <LayoutContainerContextProvider>
                        <LayoutContainer />
                    </LayoutContainerContextProvider>
                </SecureRoute>
            ),
            // Only need to set the GeneralError component at the top level.
            // Errors in children components will look for the nearest error component
            // going up the tree.
            errorElement: <GeneralError />,
            children: [
                // Using functions because eslint throws indentation errors
                ...getParcelV2Search(),
                ...getV1ParcelSearch(),
                ...getShippingPortal(),
                ...getUserManagement(),
                ...getReports(),
                {
                    element: <ShipmentTrackerLayout />,
                    children: [
                        {
                            path: 'shipment-tracker',
                            children: [
                                {
                                    path: 'stop-details',
                                    element: <StopDetails />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'delivery-management',
                    element: <DeliveryManagementLayout />,
                    children: [
                        {
                            index: true,
                            element: <DeliveryManagement />,
                        },
                    ],
                },
            ],
        },
    ];

    const router = createBrowserRouter(routes);

    useEffect(() => {
        let additionalBodyClasses = [];

        if (isPhone) additionalBodyClasses.push('viewport--phone');
        if (isTablet) additionalBodyClasses.push('viewport--tablet');

        if (!isPhone && !isTablet) additionalBodyClasses.push('viewport--desktop');

        for (const bodyClass of additionalBodyClasses) {
            document.body.classList.add(bodyClass);
        }
    }, [isPhone, isTablet]);

    return (
        <ThemeProvider>
            <QueryClientProvider client={queryClient}>
                <SnackbarProvider maxSnack={3} Components={{ loading: Loading }}>
                    <RouterProvider router={router} />
                </SnackbarProvider>
            </QueryClientProvider>
        </ThemeProvider>
    );
}
